/**
 * Shared handlers for the Quick setup blocks (plan 6.C). Every write goes through `update(step, patch)`
 * so the engine sees it; nothing here talks to the network except the template copy (block 5).
 */

import { useCallback } from "react";
import { useBuilder } from "../../state/builderContext";
import { D, E, FLAT_PERCENT, FORMAT, GROUP_DEFAULT_CAPACITY, PAYMENT_TYPE, QUICK_REFUND_OPTIONS, REFUND_TYPE, STEPS } from "../../state/constants";
import { slugify } from "../../state/hydrate";
import useScheduleOps from "../shared/useScheduleOps";

export const quickRefundValue = (details) => {
  const type = details.refund_type;
  const cutoff = parseInt(details.refund_cutoff_time, 10) || 0;
  if (type === REFUND_TYPE.PARTIAL) return "partial";
  if (type === REFUND_TYPE.NONE) return "none";
  if (type === REFUND_TYPE.FULL && cutoff === 0) return "always";
  if (type === REFUND_TYPE.FULL) return "full_24h";
  return ""; // '' on older paid events → placeholder (9.3)
};

export const paymentTiming = (details) => {
  if (details.event_prepaid_status !== E) return "at_event";
  if (details.deposit_type === PAYMENT_TYPE.DEPOSIT) return "deposit";
  return "full";
};

export default function useQuickSetup() {
  const ctx = useBuilder();
  const { state, update, dispatch } = ctx;
  const { type, details, meta } = state;

  // ---- block 1 -------------------------------------------------------------------------------------------
  const setEventName = useCallback(
    (value) => {
      const patch = { event_name: value };
      // 3.5: auto-slug until the URL was hand-edited; never once published
      if (!type.urlTouched && meta.isDraft === E) patch.event_url = slugify(value);
      update(STEPS.TYPE, patch);
      // pricing[0].item_name follows the event name until edited on Payments (6.C block 7)
      const first = (details.pricing || [])[0];
      if (meta.kind === "paid" && first && !details.pricingNameTouched) {
        update(STEPS.DETAILS, { pricing: [{ ...first, item_name: value }, ...(details.pricing || []).slice(1)] });
      }
    },
    [type.urlTouched, meta.isDraft, meta.kind, details.pricing, details.pricingNameTouched, update]
  );

  // ---- block 2 -------------------------------------------------------------------------------------------
  const setFormat = useCallback(
    (format) => {
      if (format === FORMAT.GROUP) {
        const cap = parseInt(type.no_of_attendies, 10) || 0;
        update(STEPS.TYPE, { event_format_id: FORMAT.GROUP, no_of_attendies: cap >= 2 ? cap : GROUP_DEFAULT_CAPACITY });
      } else {
        update(STEPS.TYPE, { event_format_id: FORMAT.ONE_TO_ONE, no_of_attendies: 1 });
      }
    },
    [type.no_of_attendies, update]
  );
  const setCapacity = useCallback((value) => update(STEPS.TYPE, { no_of_attendies: value === "" ? "" : parseInt(value, 10) || 0 }), [update]);

  // ---- block 3 -------------------------------------------------------------------------------------------
  const setDuration = useCallback(
    (minutes) => update(STEPS.AVAILABILITY, { event_duration_in_minutes: minutes, invitee_set_duration_status: D }), // TimeSelectorField rule
    [update]
  );

  // ---- block 4 -------------------------------------------------------------------------------------------
  const setVenue = useCallback((venueId) => update(STEPS.TYPE, { venue_id: String(venueId) }), [update]);
  const setTypeField = useCallback((patch) => update(STEPS.TYPE, patch), [update]);

  // ---- block 5 (shared with Availability & rules) --------------------------------------------------------
  const { applyTemplate, switchToCustomHours } = useScheduleOps();

  // ---- block 6 -------------------------------------------------------------------------------------------
  const setColor = useCallback((color) => update(STEPS.DETAILS, { event_color: color }), [update]);

  // ---- block 7 -------------------------------------------------------------------------------------------
  const setPrice = useCallback(
    (value) => {
      const pricing = [...(details.pricing || [])];
      const first = pricing[0] || { item_name: type.event_name, price: "", qty: 1 };
      pricing[0] = { ...first, price: value };
      update(STEPS.DETAILS, { pricing });
    },
    [details.pricing, type.event_name, update]
  );

  const setTiming = useCallback(
    (timing) => {
      if (timing === "at_event") {
        update(STEPS.DETAILS, { event_prepaid_status: D });
        return;
      }
      if (timing === "deposit") {
        update(STEPS.DETAILS, {
          event_prepaid_status: E,
          payments_type: details.payments_type || PAYMENT_TYPE.FULL,
          deposit_type: PAYMENT_TYPE.DEPOSIT,
          deposit_flat_per_type: details.deposit_flat_per_type || FLAT_PERCENT.PERCENT,
          deposit_price: details.deposit_price || "",
        });
        return;
      }
      // Full payment: first row only, price '' (today's page clears payments_price for full)
      update(STEPS.DETAILS, {
        event_prepaid_status: E,
        payments_type: PAYMENT_TYPE.FULL,
        payments_price: "",
        deposit_type: "",
        deposit_price: "",
      });
    },
    [details.payments_type, details.deposit_flat_per_type, details.deposit_price, update]
  );

  const setDepositPct = useCallback((value) => update(STEPS.DETAILS, { deposit_price: value, deposit_flat_per_type: FLAT_PERCENT.PERCENT }), [update]);

  const setQuickRefund = useCallback(
    (value) => {
      const opt = QUICK_REFUND_OPTIONS.find((o) => o.value === value);
      if (!opt) return; // 'partial' is read-only here
      update(STEPS.DETAILS, { refund_type: opt.refund_type, refund_cutoff_time: opt.refund_cutoff_time, refund_percentage: "" });
    },
    [update]
  );

  return {
    ...ctx,
    dispatch,
    setEventName,
    setFormat,
    setCapacity,
    setDuration,
    setVenue,
    setTypeField,
    applyTemplate,
    switchToCustomHours,
    setColor,
    setPrice,
    setTiming,
    setDepositPct,
    setQuickRefund,
  };
}
